import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Shell from './tuteur/Shell';
import Lecon, { Regle } from './Lecon';

// ── Exercice de formes (page PERSONNELLE, propriétaire uniquement) ───────────
// Accès via ?formes. Données : /api/formes/drill, réservé aux `user_id` de la
// table `owners` (401 / 403 → redirection silencieuse vers l'accueil, comme
// ?ecoute et ?tuteur).
//
// Ce que la page traite : le site enseigne un mot sous UNE forme, alors qu'en
// russe le même mot se présente sous dix habits. Les phrases d'exemple de la
// base en contiennent déjà des milliers ; l'exercice ne fait que les exposer.
//
// Français uniquement, comme le reste de l'espace personnel.

const TOKEN_KEY = 'motrusse_token';
const SERIF = "Georgia, 'Times New Roman', 'Noto Serif', serif";

// ── Traduction des codes de case en grammaire scolaire française ────────────
const CASE_FR = {
  nomn: 'nominatif', gent: 'génitif', datv: 'datif',
  accs: 'accusatif', ablt: 'instrumental', loct: 'prépositionnel',
};
const NUM_FR = { sg: 'singulier', pl: 'pluriel', m: 'masculin', f: 'féminin', n: 'neutre' };
const TENSE_FR = { pres: 'présent', futr: 'futur' };
const PERS_FR = { 1: '1re personne', 2: '2e personne', 3: '3e personne' };

export function slotLabel(slot) {
  if (!slot) return '';
  if (slot === 'inf') return 'infinitif';
  const [a, b] = slot.split('.');
  if (a === 'past') return b === 'pl' ? 'passé pluriel' : `passé ${NUM_FR[b]}`;
  if (a === 'impr') return `impératif ${NUM_FR[b]}`;
  if (TENSE_FR[a]) {
    const pers = PERS_FR[b[0]];
    const num = b.slice(1) === 'pl' ? 'du pluriel' : 'du singulier';
    return `${TENSE_FR[a]}, ${pers} ${num}`;
  }
  if (CASE_FR[a]) return `${CASE_FR[a]} ${NUM_FR[b] || ''}`.trim();
  return slot;
}

const PROMPT = {
  produire: 'Écris la forme qui manque.',
  forme: 'Quelle forme manque ?',
  cas: 'Cette forme, c\'est quoi ?',
  lemme: 'De quel mot vient cette forme ?',
};

// La phrase, avec la forme travaillée soit masquée (question `forme`), soit
// surlignée (les deux autres). Le découpage se fait à l'indice de caractère
// calculé côté serveur au moment de l'indexation, donc jamais par recherche de
// texte : un mot qui apparaît deux fois dans la phrase surlignerait le mauvais.
const Sentence = ({ q, reveal }) => {
  const before = q.phrase.slice(0, q.pos);
  // En mode `produire` la forme attendue n'est pas envoyée : on connaît juste sa
  // longueur, ce qui suffit à découper la phrase.
  const after = q.phrase.slice(q.pos + (q.forme ? q.forme.length : q.forme_len));
  const hidden = (q.kind === 'forme' || q.kind === 'produire') && !reveal;
  return (
    <p lang="ru" style={{ fontFamily: SERIF }} className="text-lg md:text-xl leading-relaxed text-white">
      {before}
      {hidden ? (
        <span className="inline-block align-baseline min-w-[4rem] border-b-2 border-blue-400 mx-1" aria-label="forme manquante">
          &nbsp;
        </span>
      ) : (
        <span className="text-blue-300 font-semibold">{q.forme || reveal}</span>
      )}
      {after}
    </p>
  );
};

// Cours d'entrée de l'exercice de formes. Volontairement court : six cas, six
// lignes. Ce n'est pas une grammaire, c'est de quoi ne pas répondre à l'aveugle.
const CoursFormes = () => (
  <Lecon id="formes" titre="Avant de commencer : à quoi sert cet exercice">
    <p>
      En russe, un mot change de forme selon sa fonction dans la phrase. Tu connais
      déjà beaucoup de mots, mais tu les as appris sous UNE forme. Ici tu les
      retrouves déguisés, dans de vraies phrases.
    </p>
    <p className="text-slate-400">
      Trois types de question : compléter la phrase avec la bonne forme, nommer le
      cas d'une forme surlignée, ou retrouver le mot de base. Pour la première, les
      quatre propositions sont toutes des formes du même mot : c'est la terminaison
      qui décide, pas le sens.
    </p>
    <div className="flex flex-col gap-1.5 border-t border-slate-700/60 pt-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">Les six cas, en une ligne chacun</p>
      <Regle terme="Nominatif" quand="le sujet, celui qui fait l'action" exemple="кто? что? — Брат читает." />
      <Regle terme="Génitif" quand="l'appartenance, l'absence, après beaucoup de prépositions" exemple="кого? чего? — книга брата, нет времени, у окна" />
      <Regle terme="Datif" quand="le destinataire, à qui l'on donne ou dit" exemple="кому? чему? — я звоню брату, по улице" />
      <Regle terme="Accusatif" quand="l'objet direct, et la direction avec в/на" exemple="кого? что? — я вижу брата, еду в Москву" />
      <Regle terme="Instrumental" quand="le moyen, l'accompagnement avec с" exemple="кем? чем? — пишу ручкой, с братом" />
      <Regle terme="Prépositionnel" quand="le lieu, jamais sans préposition" exemple="о ком? где? — о брате, в Москве" />
    </div>
    <p className="text-slate-400">
      Le réflexe qui paie : regarde d'abord la <strong className="text-slate-200">préposition</strong> et le
      <strong className="text-slate-200"> verbe</strong> qui commandent le mot. В + accusatif répond à « où va-t-on »,
      в + prépositionnel à « où est-on ». C'est presque toujours là que se joue la réponse.
    </p>
    <div className="flex flex-col gap-1.5 border-t border-slate-700/60 pt-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">
        Les terminaisons, au singulier — c'est ça qu'il faut fabriquer
      </p>
      <p className="text-slate-400 text-[13px]">
        Trois familles suffisent à couvrir l'immense majorité des noms. Repère la finale du
        mot au nominatif, et applique.
      </p>
      <Regle terme="masculin" quand="consonne · дом" exemple="дом · дома · дому · дом · домом · доме" />
      <Regle terme="féminin -а" quand="Москва, работа" exemple="-а · -ы · -е · -у · -ой · -е" />
      <Regle terme="neutre -о" quand="слово, окно" exemple="-о · -а · -у · -о · -ом · -е" />
      <p className="text-slate-500 text-[13px]">
        Dans l'ordre : nominatif, génitif, datif, accusatif, instrumental, prépositionnel.
        Deux repères qui font gagner beaucoup : le <strong className="text-slate-200">prépositionnel est
        presque toujours en -е</strong>, et l'<strong className="text-slate-200">instrumental en -ом / -ой</strong>.
      </p>
      <p className="text-slate-500 text-[13px]">
        Au pluriel, le génitif est le plus capricieux (домов, работ, слов) : c'est celui qu'il
        faut voir souvent plutôt que déduire. Les tableaux te donnent chaque modèle en entier.
      </p>
    </div>
    <p className="text-slate-500 text-[13px]">
      Pour les verbes, on te demande la personne et le temps. Rappel : un verbe perfectif
      n'a pas de présent, ses formes personnelles sont un futur.
    </p>
  </Lecon>
);

export default function FormsDrill({ cursive, toggleCursive }) {
  const [phase, setPhase] = useState('loading'); // loading | error | ready | done
  const [questions, setQuestions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [i, setI] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [essai, setEssai] = useState('');
  const [indice, setIndice] = useState(false);
  const inputRef = useRef(null);
  const [score, setScore] = useState({ ok: 0, total: 0 });
  const [missed, setMissed] = useState([]);
  const [token] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  });

  const load = useCallback(() => {
    const leave = () => { try { window.location.replace('/'); } catch {} };
    if (!token) { leave(); return; }
    setPhase('loading');
    fetch('/api/formes/drill?n=10', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.status === 401 || r.status === 403) { leave(); return null; }
        if (!r.ok) throw new Error('http');
        return r.json();
      })
      .then((d) => {
        if (d == null) return;
        setQuestions(d.questions || []);
        setMeta({ level_rank: d.level_rank, from_mine: d.from_mine });
        setI(0); setChosen(null); setEssai(''); setIndice(false);
        setScore({ ok: 0, total: 0 }); setMissed([]);
        setPhase('ready');
      })
      .catch(() => setPhase('error'));
  }, [token]);

  useEffect(load, [load]);

  const q = questions[i];

  const answer = useCallback((opt) => {
    if (chosen != null || !q) return;
    const correct = opt === q.answer;
    setChosen(opt);
    setScore((s) => ({ ok: s.ok + (correct ? 1 : 0), total: s.total + 1 }));
    if (!correct) setMissed((m) => [...m, q]);
    // Journal en base : c'est lui qui permettra au tuteur de dire quelle case
    // tient et laquelle non. Sans attente, l'exercice ne dépend pas du réseau.
    if (token) {
      fetch('/api/formes/answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          kind: q.kind, word_id: q.word_id, slot: q.slot, correct, chosen: opt,
        }),
      }).catch(() => {});
    }
  }, [chosen, q, token]);

  // Correction d'une forme TAPÉE : le serveur détient la réponse, il la renvoie
  // avec la distance pour distinguer « faux » de « il manque une lettre ».
  const valider = useCallback(() => {
    if (!q || chosen != null || !essai.trim()) return;
    fetch('/api/formes/produire', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ctx: q.ctx, essai }),
    })
      // Une session expirée renvoie 403 avec un corps d'erreur : sans ce
      // contrôle, `d.ok` valait `undefined` et la réponse était comptée FAUSSE.
      // On préfère ne rien compter et renvoyer à l'accueil, comme partout.
      .then((r) => {
        if (r.status === 401 || r.status === 403) {
          try { window.location.replace('/'); } catch {}
          return null;
        }
        if (!r.ok) throw new Error('http');
        return r.json();
      })
      .then((d) => {
        if (d == null) return;
        setChosen({ ...d, tape: essai });
        setScore((s) => ({ ok: s.ok + (d.ok ? 1 : 0), total: s.total + 1 }));
        if (!d.ok) setMissed((m) => [...m, { ...q, forme: d.forme, tape: essai, presque: d.presque }]);
      })
      .catch(() => {});
  }, [q, chosen, essai, token]);

  const next = useCallback(() => {
    setChosen(null); setEssai(''); setIndice(false);
    setTimeout(() => inputRef.current?.focus(), 60);
    if (i + 1 >= questions.length) setPhase('done');
    else setI(i + 1);
  }, [i, questions.length]);

  const label = useCallback((opt) => (q?.kind === 'cas' ? slotLabel(opt) : opt), [q]);

  const byCase = useMemo(() => {
    const acc = {};
    missed.forEach((m) => { if (m.slot) acc[m.slot] = (acc[m.slot] || 0) + 1; });
    return Object.entries(acc).sort((a, b) => b[1] - a[1]);
  }, [missed]);

  if (phase === 'loading') {
    return (
      <Shell actif="/?formes" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell actif="/?formes" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="text-center flex flex-col items-center gap-4 pt-16">
          <p className="text-slate-400">Impossible de charger l'exercice.</p>
          <button onClick={load} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors">
            Réessayer
          </button>
        </div>
      </Shell>
    );
  }

  if (phase === 'done' || !q) {
    return (
      <Shell actif="/?formes" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="flex flex-col gap-5">
          <h1 className="text-xl md:text-2xl font-bold">Série terminée</h1>
          {score.total > 0 ? (
            <p className="text-slate-300">
              {score.ok} sur {score.total}.
              {byCase.length > 0 && (
                <> Ce qui a coincé : {byCase.map(([s, n]) => `${slotLabel(s)}${n > 1 ? ` (${n})` : ''}`).join(', ')}.</>
              )}
            </p>
          ) : (
            <p className="text-slate-400">
              Pas encore de matière pour cet exercice. Travaille quelques mots au quiz, ils
              reviendront ici sous toutes leurs formes.
            </p>
          )}
          {missed.length > 0 && (
            <div className="flex flex-col gap-3">
              {missed.map((m, k) => (
                <div key={k} className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex flex-col gap-1">
                  <Sentence q={m} reveal />
                  <p className="text-sm text-slate-400">{m.trad}</p>
                  <p className="text-xs text-slate-500">
                    <span lang="ru" style={{ fontFamily: SERIF }} className="text-blue-300">{m.lemma}</span>
                    {' '}({m.sens}){m.slot ? ` au ${slotLabel(m.slot)}` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-3">
            <button onClick={load} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors">
              Une autre série
            </button>
            <a href="/?tuteur" className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg font-medium transition-colors">
              Retour au tuteur
            </a>
          </div>
        </div>
      </Shell>
    );
  }

  // `chosen` est une CHAÎNE pour les questions à choix, un OBJET pour la forme
  // tapée (le serveur renvoie ok/presque/distance/forme).
  const produire = q.kind === 'produire';
  const correct = produire ? !!chosen?.ok : (chosen != null && chosen === q.answer);

  return (
    <Shell actif="/?formes" cursive={cursive} toggleCursive={toggleCursive}>
      <CoursFormes />

      <div className="flex items-baseline justify-between">
        <h1 className="text-xl md:text-2xl font-bold">Les formes</h1>
        <span className="text-xs text-slate-500 tabular-nums">
          {i + 1} / {questions.length}
          {score.total > 0 && ` · ${score.ok} juste${score.ok > 1 ? 's' : ''}`}
        </span>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 flex flex-col gap-4">
        <p className="text-sm text-slate-400">{PROMPT[q.kind]}</p>
        <Sentence q={produire && chosen ? { ...q, forme: chosen.forme } : q} reveal={chosen != null} />
        {q.kind !== 'lemme' && (
          <p className="text-sm text-slate-500">
            <span lang="ru" style={{ fontFamily: SERIF }} className="text-blue-300">{q.lemma}</span>
            {' · '}{q.sens}
          </p>
        )}

        {produire ? (
          <div className="flex flex-col gap-3">
            <input
              ref={inputRef}
              lang="ru"
              value={essai}
              onChange={(e) => setEssai(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') (chosen ? next() : valider()); }}
              disabled={chosen != null}
              spellCheck="false"
              autoComplete="off"
              placeholder="écris la forme…"
              style={{ fontFamily: SERIF }}
              className={`w-full bg-slate-900 border rounded-xl px-4 py-3 text-lg text-white placeholder-slate-600 outline-none transition-colors ${
                chosen ? (chosen.ok ? 'border-green-500' : 'border-red-500') : 'border-slate-700 focus:border-blue-500'
              }`}
            />
            {chosen == null && (
              <div className="flex items-center gap-3 flex-wrap">
                <button onClick={valider} disabled={!essai.trim()}
                  className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-sm font-medium transition-colors">
                  Valider
                </button>
                <button onClick={() => setIndice(true)} className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                  Un indice
                </button>
                {indice && (
                  <span className="text-sm text-slate-400">
                    {q.slot ? `${slotLabel(q.slot)} · ` : ''}
                    <span lang="ru" style={{ fontFamily: SERIF }} className="text-blue-300">{q.initiale}</span>
                    {' · '}{q.lettres} lettres
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-2">
          {q.options.map((opt) => {
            const isAnswer = opt === q.answer;
            const picked = chosen === opt;
            let cls = 'bg-slate-700 hover:bg-blue-600 border-slate-600 hover:border-blue-500';
            if (chosen != null) {
              if (isAnswer) cls = 'bg-green-600/80 border-green-500';
              else if (picked) cls = 'bg-red-600/80 border-red-500';
              else cls = 'bg-slate-800 border-slate-700 text-slate-500';
            }
            const ru = q.kind !== 'cas';
            return (
              <button
                key={opt}
                onClick={() => answer(opt)}
                disabled={chosen != null}
                lang={ru ? 'ru' : undefined}
                style={ru ? { fontFamily: SERIF } : undefined}
                className={`px-4 py-3 rounded-xl border text-base transition-all break-words ${cls} ${chosen == null ? 'active:scale-95' : ''}`}
              >
                {label(opt)}
              </button>
            );
          })}
        </div>
        )}

        {chosen != null && (
          <div className="flex flex-col gap-2 border-t border-slate-700 pt-3">
            {produire && (
              <p className="text-sm">
                {chosen.ok ? <span className="text-green-400">Juste.</span>
                  : chosen.presque ? <span className="text-amber-400">Presque : {chosen.distance} lettre{chosen.distance > 1 ? 's' : ''} d'écart.</span>
                  : <span className="text-red-400">Non.</span>}
                {!chosen.ok && (
                  <>
                    {' '}
                    <span lang="ru" style={{ fontFamily: SERIF }} className="text-blue-300 text-lg">{chosen.forme}</span>
                  </>
                )}
              </p>
            )}
            <p className="text-sm text-slate-300">{q.trad}</p>
            {q.slot && (
              <p className="text-sm text-slate-400">
                <span lang="ru" style={{ fontFamily: SERIF }} className="text-blue-300">{q.forme}</span>
                {' = '}{q.lemma} au {slotLabel(q.slot)}.
              </p>
            )}
            {!q.slot && (
              // Cas assumé : hors contexte, l'analyse hésite entre deux cases (дома
              // = génitif singulier ou nominatif pluriel). On ne nomme donc rien
              // plutôt que de risquer d'enseigner une étiquette fausse.
              <p className="text-sm text-slate-400">
                <span lang="ru" style={{ fontFamily: SERIF }} className="text-blue-300">{q.forme}</span>
                {' '}est une forme de {q.lemma}. Plusieurs cas donnent la même forme ici.
              </p>
            )}
            <button
              onClick={next}
              autoFocus
              className={`self-start px-5 py-2.5 rounded-lg font-medium transition-colors ${correct ? 'bg-blue-600 hover:bg-blue-500' : 'bg-slate-700 hover:bg-slate-600'}`}
            >
              {i + 1 >= questions.length ? 'Voir le bilan' : 'Suivant'}
            </button>
          </div>
        )}
      </div>

      {meta && meta.from_mine < questions.length && (
        <p className="text-xs text-slate-600">
          {meta.from_mine} question{meta.from_mine > 1 ? 's' : ''} sur des mots que tu travailles déjà,
          le reste sur des mots de ton niveau (rang {meta.level_rank} et en dessous).
        </p>
      )}
    </Shell>
  );
}
